package com.heelion.farmafreelance.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.heelion.farmafreelance.agent.MesePagamenti
import com.heelion.farmafreelance.agent.PagamentiFarmacia
import com.heelion.farmafreelance.agent.ResocontoPagamenti
import com.heelion.farmafreelance.models.StoricoEntry
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.math.round

private val VERDE = Color(0xFF2E7D32)
private val ARANCIO = Color(0xFFE65100)

/**
 * "Resoconto pagamenti": agenda personale del farmacista. Per ogni mese e farmacia i turni svolti,
 * ore e importo; si biffa "pagato" (per farmacia o per singolo turno) quando si è ricevuto il
 * pagamento, e si può correggere la data. La farmacia non vede niente di tutto questo.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PagamentiScreen(viewModel: AuthViewModel, onBack: () -> Unit) {
    var storico by remember { mutableStateOf<List<StoricoEntry>?>(null) }
    var errore by remember { mutableStateOf<String?>(null) }
    var soloDaIncassare by remember { mutableStateOf(false) }
    var aperta by remember { mutableStateOf<String?>(null) } // "anno-mese-farmacia" con i turni visibili
    var dataDaCorreggere by remember { mutableStateOf<List<StoricoEntry>?>(null) }
    val scope = rememberCoroutineScope()

    suspend fun ricarica() {
        val s = viewModel.getStoricoOppureNull()
        if (s == null) errore = "Non riesco a caricare i turni svolti: controlla la connessione e riprova." else { storico = s; errore = null }
    }
    LaunchedEffect(Unit) { ricarica() }

    /** Segna o toglie "pagato": subito sullo schermo, poi sul server (se fallisce si torna indietro). */
    fun segna(turni: List<StoricoEntry>, pagato: Boolean, data: Instant? = null) {
        val ids = turni.mapNotNull { it.id }.toSet()
        val prima = storico
        val quando = if (pagato) data ?: Clock.System.now() else null
        storico = storico?.map { if (it.id in ids) it.copy(pagatoIl = quando) else it }
        scope.launch {
            if (!viewModel.segnaPagato(ids.toList(), pagato, quando?.toEpochMilliseconds())) {
                storico = prima
                errore = "Non sono riuscito a salvare, riprova."
            } else errore = null
        }
    }

    dataDaCorreggere?.let { turni ->
        val stato = rememberDatePickerState(initialSelectedDateMillis = turni.mapNotNull { it.pagatoIl }.maxOrNull()?.toEpochMilliseconds())
        DatePickerDialog(
            onDismissRequest = { dataDaCorreggere = null },
            confirmButton = {
                TextButton(onClick = {
                    stato.selectedDateMillis?.let { segna(turni, true, Instant.fromEpochMilliseconds(it)) }
                    dataDaCorreggere = null
                }) { Text("OK") }
            },
            dismissButton = { TextButton(onClick = { dataDaCorreggere = null }) { Text("Annulla") } }
        ) { DatePicker(state = stato, title = { Text("Quando sei stato pagato?", modifier = Modifier.padding(16.dp)) }) }
    }

    Scaffold(
        containerColor = Color.Transparent,
        topBar = {
            TopAppBar(
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent),
                title = { Text("Resoconto pagamenti", fontWeight = FontWeight.Bold) },
                navigationIcon = { IconButton(onClick = onBack) { Icon(AppIcons.ArrowBack, contentDescription = "Indietro") } }
            )
        }
    ) { padding ->
        Column(Modifier.fillMaxSize().padding(padding).padding(horizontal = 16.dp)) {
            val s = storico
            when {
                s == null && errore != null -> Text(errore!!, color = MaterialTheme.colorScheme.error)
                s == null -> Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
                s.isEmpty() -> Text("Non hai ancora turni svolti. Quando ne avrai, qui potrai segnare quali farmacie ti hanno pagato.")
                else -> {
                    val totaleDaIncassare = arrotonda(s.filter { it.pagatoIl == null }.sumOf { ResocontoPagamenti.importo(it) })
                    Text(
                        "La tua agenda personale: quando una farmacia ti paga, biffala. La farmacia non vede niente di questo.",
                        style = MaterialTheme.typography.bodySmall, color = Color.Gray
                    )
                    Spacer(Modifier.height(8.dp))
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            if (totaleDaIncassare > 0) "Da incassare in tutto: ${euro(totaleDaIncassare)}" else "Tutto incassato",
                            style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold,
                            color = if (totaleDaIncassare > 0) ARANCIO else VERDE, modifier = Modifier.weight(1f)
                        )
                        FilterChip(selected = soloDaIncassare, onClick = { soloDaIncassare = !soloDaIncassare }, label = { Text("Solo da incassare") })
                    }
                    errore?.let { Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall) }
                    Spacer(Modifier.height(8.dp))

                    val mesi = ResocontoPagamenti.perMese(s).mapNotNull { m ->
                        if (!soloDaIncassare) m else m.copy(farmacie = m.farmacie.filter { !it.tuttoPagato }).takeIf { it.farmacie.isNotEmpty() }
                    }
                    if (mesi.isEmpty()) Text("Nessun pagamento da incassare.", modifier = Modifier.padding(vertical = 16.dp))
                    LazyColumn(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        items(mesi, key = { "${it.anno}-${it.mese}" }) { m ->
                            SchedaMesePagamenti(
                                m, aperta,
                                onApri = { chiave -> aperta = if (aperta == chiave) null else chiave },
                                onSegna = { turni, pagato -> segna(turni, pagato) },
                                onCorreggiData = { dataDaCorreggere = it }
                            )
                        }
                        item { Spacer(Modifier.height(24.dp)) }
                    }
                }
            }
        }
    }
}

@Composable
private fun SchedaMesePagamenti(
    m: MesePagamenti,
    aperta: String?,
    onApri: (String) -> Unit,
    onSegna: (List<StoricoEntry>, Boolean) -> Unit,
    onCorreggiData: (List<StoricoEntry>) -> Unit
) {
    Card(shape = RoundedCornerShape(12.dp), colors = CardDefaults.cardColors(containerColor = Color.White), modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.padding(12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text("${MESI[m.mese - 1]} ${m.anno}", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                Text(
                    if (m.daIncassare > 0) "Da incassare: ${euro(m.daIncassare)}" else "Tutto incassato",
                    style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold,
                    color = if (m.daIncassare > 0) ARANCIO else VERDE
                )
            }
            m.farmacie.forEach { f ->
                val chiave = "${m.anno}-${m.mese}-${f.farmaciaId}"
                HorizontalDivider(Modifier.padding(vertical = 6.dp))
                RigaFarmacia(f, aperta == chiave, onApri = { onApri(chiave) }, onSegna = onSegna, onCorreggiData = onCorreggiData)
            }
        }
    }
}

@Composable
private fun RigaFarmacia(
    f: PagamentiFarmacia,
    aperta: Boolean,
    onApri: () -> Unit,
    onSegna: (List<StoricoEntry>, Boolean) -> Unit,
    onCorreggiData: (List<StoricoEntry>) -> Unit
) {
    Column {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().clickable(onClick = onApri)) {
            // Biffando la farmacia si segnano tutti i suoi turni del mese; se solo alcuni sono pagati la casella è a metà.
            TriStateCheckbox(
                state = when {
                    f.tuttoPagato -> androidx.compose.ui.state.ToggleableState.On
                    f.nessunoPagato -> androidx.compose.ui.state.ToggleableState.Off
                    else -> androidx.compose.ui.state.ToggleableState.Indeterminate
                },
                onClick = { onSegna(f.turni, !f.tuttoPagato) }
            )
            Column(Modifier.weight(1f)) {
                Text(f.nomeFarmacia, fontWeight = FontWeight.Bold, color = if (f.tuttoPagato) VERDE else Color.Unspecified)
                Text(
                    "${f.turni.size} ${if (f.turni.size == 1) "turno" else "turni"} · ${numero(f.ore)} h · ${euro(f.importo)}",
                    style = MaterialTheme.typography.bodySmall, color = Color.Gray
                )
                f.pagatoIl?.let { data ->
                    Text(
                        (if (f.tuttoPagato) "Pagato il " else "Pagato in parte · ultimo il ") + dataBreve(data) + " · modifica",
                        style = MaterialTheme.typography.bodySmall, color = VERDE,
                        modifier = Modifier.clickable { onCorreggiData(f.turni.filter { it.pagatoIl != null }) }
                    )
                }
            }
            Icon(AppIcons.ArrowDropDown, contentDescription = if (aperta) "Chiudi" else "Apri", tint = Color.Gray,
                modifier = Modifier.rotate(if (aperta) 0f else -90f))
        }
        if (aperta) {
            f.turni.forEach { t ->
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().padding(start = 32.dp)) {
                    Checkbox(checked = t.pagatoIl != null, onCheckedChange = { onSegna(listOf(t), it) })
                    Column(Modifier.weight(1f)) {
                        val g = ResocontoPagamenti.giorno(t)
                        Text("${g.dayOfMonth}/${g.monthNumber} · ${t.oraInizio}-${t.oraFine}", style = MaterialTheme.typography.bodyMedium)
                        t.pagatoIl?.let {
                            Text("Pagato il ${dataBreve(it)} · modifica", style = MaterialTheme.typography.bodySmall, color = VERDE,
                                modifier = Modifier.clickable { onCorreggiData(listOf(t)) })
                        }
                    }
                    Text(euro(ResocontoPagamenti.importo(t)), style = MaterialTheme.typography.bodyMedium)
                }
            }
        }
    }
}

private val MESI = listOf("Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio",
    "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre")

private fun dataBreve(i: Instant): String = i.toLocalDateTime(TimeZone.currentSystemDefault()).date.let { "${it.dayOfMonth}/${it.monthNumber}/${it.year}" }

private fun arrotonda(v: Double) = round(v * 100) / 100

private fun numero(v: Double): String =
    if (v % 1.0 == 0.0) v.toLong().toString() else (round(v * 100) / 100).toString().replace('.', ',')

/** "1.470,50 €" */
private fun euro(v: Double): String {
    val centesimi = round(v * 100).toLong()
    val interi = (centesimi / 100).toString().reversed().chunked(3).joinToString(".").reversed()
    return "$interi,${(centesimi % 100).toString().padStart(2, '0')} €"
}
